import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  BellRing,
  ChevronDown,
  ChevronRight,
  Search,
  Mail,
  MailCheck,
  ExternalLink,
  Calendar
} from 'lucide-react';
import { useAlertTriggered, AlertTriggeredFilters } from '../../hooks/useAlertTriggered';
import { AlertType, ALERT_TYPE_INFO } from '../../types/alerts';

interface TriggeredAlertsListProps {
  onProductClick?: (productId: string) => void;
}

export const TriggeredAlertsList: React.FC<TriggeredAlertsListProps> = ({
  onProductClick
}) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AlertTriggeredFilters>({});
  const [expandedTomas, setExpandedTomas] = useState<Set<number>>(new Set());
  const [expandedAlerts, setExpandedAlerts] = useState<Set<string>>(new Set());

  const {
    alertsByToma,
    tomasWithAlerts,
    presentAlertTypes,
    loading,
    error,
    stats
  } = useAlertTriggered(filters);

  // Toggle expansión de toma
  const toggleToma = (tomaId: number) => {
    setExpandedTomas(prev => {
      const next = new Set(prev);
      if (next.has(tomaId)) {
        next.delete(tomaId);
      } else {
        next.add(tomaId);
      }
      return next;
    });
  };

  // Toggle expansión de alerta
  const toggleAlert = (alertId: string) => {
    setExpandedAlerts(prev => {
      const next = new Set(prev);
      if (next.has(alertId)) {
        next.delete(alertId);
      } else {
        next.add(alertId);
      }
      return next;
    });
  };

  // Obtener label del tipo de alerta
  const getAlertTypeLabel = (type: AlertType): string => {
    const info = ALERT_TYPE_INFO.find(t => t.type === type);
    return info ? t(info.labelKey) : type;
  };

  // Formatear precio
  const formatPrice = (price: number | undefined): string => {
    if (price === undefined) return '-';
    return `$${price.toLocaleString('es-AR')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-red-500">
        <Bell className="h-5 w-5 mr-2" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap gap-3 p-4 bg-gray-50 rounded-lg">
        {/* Búsqueda */}
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={filters.searchTerm || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value || undefined }))}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              placeholder={t('alerts.search_placeholder')}
            />
          </div>
        </div>

        {/* Filtro por toma */}
        <div className="min-w-[150px]">
          <select
            value={filters.tomaId || ''}
            onChange={(e) => setFilters(prev => ({
              ...prev,
              tomaId: e.target.value ? Number(e.target.value) : undefined
            }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="">{t('alerts.all_tomas')}</option>
            {tomasWithAlerts.map(tomaId => (
              <option key={tomaId} value={tomaId}>
                Toma {tomaId}
              </option>
            ))}
          </select>
        </div>

        {/* Filtro por tipo */}
        <div className="min-w-[180px]">
          <select
            value={filters.alertType || ''}
            onChange={(e) => setFilters(prev => ({
              ...prev,
              alertType: e.target.value as AlertType || undefined
            }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="">{t('alerts.all_types')}</option>
            {presentAlertTypes.map(type => (
              <option key={type} value={type}>
                {getAlertTypeLabel(type)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.totalAlerts}</p>
          <p className="text-xs text-gray-500">{t('alerts.total_triggered')}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-orange-600">{stats.totalProducts}</p>
          <p className="text-xs text-gray-500">{t('alerts.products_affected')}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.emailsSent}</p>
          <p className="text-xs text-gray-500">{t('alerts.emails_sent')}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-purple-600">{stats.tomasCount}</p>
          <p className="text-xs text-gray-500">{t('alerts.tomas_with_alerts')}</p>
        </div>
      </div>

      {/* Lista agrupada por toma */}
      {alertsByToma.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <Bell className="h-12 w-12 mb-3" />
          <p className="text-lg font-medium">{t('alerts.no_triggered_alerts')}</p>
          <p className="text-sm">{t('alerts.no_triggered_alerts_desc')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alertsByToma.map(({ tomaId, tomaDate, alerts, totalTriggerCount }) => (
            <div key={tomaId} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Header de toma */}
              <button
                onClick={() => toggleToma(tomaId)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  {expandedTomas.has(tomaId) ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-900">Toma {tomaId}</span>
                    {tomaDate && (
                      <span className="text-sm text-gray-500">({tomaDate})</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500">
                    {alerts.length} {t('alerts.alerts_count')}
                  </span>
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                    {totalTriggerCount} {t('alerts.products')}
                  </span>
                </div>
              </button>

              {/* Lista de alertas de la toma */}
              {expandedTomas.has(tomaId) && (
                <div className="border-t border-gray-200">
                  {alerts.map((alert) => (
                    <div key={alert.id} className="border-b border-gray-100 last:border-b-0">
                      {/* Header de alerta */}
                      <button
                        onClick={() => toggleAlert(alert.id!)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          {expandedAlerts.has(alert.id!) ? (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )}
                          <BellRing className="h-4 w-4 text-orange-500" />
                          <div className="text-left">
                            <p className="font-medium text-gray-900">{alert.alertName}</p>
                            <p className="text-xs text-gray-500">{getAlertTypeLabel(alert.alertType)}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {alert.emailSent ? (
                            <span title={t('alerts.email_sent')}>
                              <MailCheck className="h-4 w-4 text-green-500" />
                            </span>
                          ) : alert.emailRecipients && alert.emailRecipients.length > 0 ? (
                            <span title={t('alerts.email_pending')}>
                              <Mail className="h-4 w-4 text-gray-400" />
                            </span>
                          ) : null}
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                            {alert.triggerCount} {t('alerts.products')}
                          </span>
                        </div>
                      </button>

                      {/* Productos que dispararon la alerta */}
                      {expandedAlerts.has(alert.id!) && (
                        <div className="px-4 pb-3 bg-gray-50">
                          <div className="space-y-2 mt-2">
                            {alert.triggeredProducts.map((product, idx) => (
                              <div
                                key={`${product.id_catalogo}-${idx}`}
                                className="flex items-center p-2 bg-white rounded-lg border border-gray-200"
                              >
                                {product.imagen && (
                                  <img
                                    src={product.imagen}
                                    alt=""
                                    className="h-12 w-12 object-cover rounded mr-3"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {product.title}
                                  </p>
                                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                                    <span>{product.sitio}</span>
                                    <span>•</span>
                                    <span className="font-medium text-gray-700">
                                      {formatPrice(product.currentPrice)}
                                    </span>
                                    {product.previousPrice !== undefined && (
                                      <>
                                        <span>←</span>
                                        <span className="text-gray-400">
                                          {formatPrice(product.previousPrice)}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  <p className="text-xs text-orange-600 mt-1">
                                    {product.triggerReason}
                                  </p>
                                </div>
                                {onProductClick && (
                                  <button
                                    onClick={() => onProductClick(product.id_catalogo)}
                                    className="ml-2 p-1 text-gray-400 hover:text-blue-500 transition-colors"
                                    title={t('alerts.view_product')}
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
